import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import storage from "../../util/storage";
import Authentication from "../../../oauth/Authentication";
import { roleBackOfficeList } from "../../../domain/User/Erole";

interface Props {
  readOnly?: boolean;
}

interface SwitchableEntry {
  id: string | null;
  role: string;
  name: string;
  code: string;
}

const RoleSwitcher = ({ readOnly = false }: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const user = storage.getUserInformation();
  const accounts = storage.getAllowedAccounts();
  const allowedRoles = storage.getAllowedRoles();
  const currentRole = storage.getRole();
  const currentAccountId = storage.getAccountId();

  const userDisplayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim() ||
    user?.username ||
    "";
  const backOfficeRolesFromAccounts = new Set(
    accounts
      .map((account) => account.role)
      .filter((role) => roleBackOfficeList.includes(role)),
  );
  const virtualEntries: SwitchableEntry[] = allowedRoles
    .filter(
      (role) =>
        roleBackOfficeList.includes(role) &&
        !backOfficeRolesFromAccounts.has(role),
    )
    .map((role) => ({
      id: null,
      role,
      name: userDisplayName,
      code: t(`role.${role}`),
    }));
  const entries: SwitchableEntry[] = [...accounts, ...virtualEntries];

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  if (!user) {
    return null;
  }

  const currentEntry = entries.find(
    (entry) =>
      (entry.id ?? null) === (currentAccountId ?? null) &&
      entry.role === currentRole,
  );
  const isReadOnly = readOnly || entries.length <= 1;
  const canSwitch = !isReadOnly;

  const fallbackName =
    [user.firstname, user.lastname].filter(Boolean).join(" ").trim() ||
    user.username;
  const triggerName = currentEntry?.name || fallbackName;
  const triggerCode = currentEntry?.code;

  const handleSelect = async (entry: SwitchableEntry): Promise<void> => {
    setIsOpen(false);
    if (
      (entry.id ?? null) === (currentAccountId ?? null) &&
      entry.role === currentRole
    ) {
      return;
    }
    storage.setRole(entry.role);
    if (entry.id) {
      storage.setAccountId(entry.id);
    } else {
      storage.clearAccountId();
    }
    await Authentication.refreshProfile();
    window.location.replace("/tableau-de-bord");
  };

  const toggle = () => {
    if (canSwitch) setIsOpen((prev) => !prev);
  };

  return (
    <div
      ref={containerRef}
      className={`role-switcher ${isOpen ? "role-switcher--open" : ""} ${isReadOnly ? "role-switcher--readonly" : ""}`}
    >
      <button
        type="button"
        className="role-switcher__trigger"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t("role.open-switcher")}
        disabled={!canSwitch}
      >
        <span className="role-switcher__identity">
          <span className="role-switcher__name">{triggerName}</span>
          {triggerCode && (
            <span className="role-switcher__code">{triggerCode}</span>
          )}
        </span>
        {canSwitch && (
          <svg
            className="role-switcher__caret"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M2 4l4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {isOpen && canSwitch && (
        <ul
          className="role-switcher__menu"
          role="listbox"
          aria-label={t("role.open-switcher")}
        >
          {entries.map((entry) => {
            const isActive =
              (entry.id ?? null) === (currentAccountId ?? null) &&
              entry.role === currentRole;
            return (
              <li
                key={`${entry.id ?? "virtual"}-${entry.role}`}
                role="option"
                aria-selected={isActive}
              >
                <button
                  type="button"
                  className={`role-switcher__item ${isActive ? "role-switcher__item--active" : ""}`}
                  onClick={() => handleSelect(entry)}
                >
                  <span className="role-switcher__item-main">
                    <span className="role-switcher__item-name">
                      {entry.name}
                    </span>
                    {entry.code && (
                      <span className="role-switcher__item-code">
                        {entry.code}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RoleSwitcher;
